import React, { useEffect, useState, useCallback } from "react";
import { Play, Pause, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { http, apiError } from "@/lib/api";
import { useOrg } from "@/context/OrgContext";
import { useAuth } from "@/context/AuthContext";
import { Card, Spinner } from "@/components/Kit";

const STATE_C = { active: "#2E7D32", degraded: "#E39629", error: "#A5300F", paused: "#8A8175" };
const TYPE_LABEL = {
  dns_registrar: "DNS / Registrar", vuln_intel: "Vulnerability Intel", bgp: "BGP / RPKI Routing",
  ransomware: "Ransomware Leak-Site", ct: "Certificate Transparency",
  lookalike: "Lookalike / New-Domain Radar", github_leak: "GitHub Code Leakage",
};

export default function Watchers() {
  const { orgId } = useOrg();
  const { can } = useAuth();
  const canManage = can("monitor.watchers", "manage");
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(null);

  const load = useCallback(() => http.get("/monitoring/watchers", { params: { org_id: orgId } }).then((r) => setRows(r.data)), [orgId]);
  useEffect(() => { setRows([]); }, [orgId]);
  useEffect(() => { if (orgId) load(); }, [load, orgId]);

  const runNow = async (w) => {
    setBusy(w.id);
    try { await http.post(`/monitoring/watchers/${w.id}/run`, {}, { params: { org_id: orgId } }); toast.success("Watcher ran"); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBusy(null); }
  };
  const toggle = async (w) => {
    try { await http.patch(`/monitoring/watchers/${w.id}`, { enabled: !w.enabled }, { params: { org_id: orgId } }); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const setInterval = async (w, v) => {
    try { await http.patch(`/monitoring/watchers/${w.id}`, { interval_minutes: Number(v) }, { params: { org_id: orgId } }); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  return (
    <div className="p-6 max-w-[1500px] mx-auto fade-up">
      <h1 className="font-display text-3xl font-bold text-[#A5300F]">Watchers</h1>
      <p className="text-sm text-[#8A8175] mt-1 mb-4">{rows.length} configured watchers across the shared monitoring engine.</p>
      <Card>
        <table className="w-full text-sm" data-testid="watchers-table">
          <thead><tr className="text-[11px] uppercase tracking-wider text-[#8A8175] text-left bg-[#FFF5EB] border-b border-[#ECE6DD]">
            <th className="py-2.5 px-4">Type</th><th>Subject</th><th className="text-center">Interval (min)</th><th className="text-center">State</th>
            <th>Last success</th><th className="text-center">Alerts 30d</th><th className="text-center">Precision</th>{canManage && <th className="text-right pr-4">Actions</th>}
          </tr></thead>
          <tbody>
            {rows.map((w) => (
              <tr key={w.id} className="border-b border-[#F4EFE8] hover:bg-[#FBF7F1]" data-testid={`watcher-row-${w.id}`}>
                <td className="py-2.5 px-4 font-semibold">{TYPE_LABEL[w.watcher_type] || w.watcher_type}</td>
                <td className="text-[#5C554C] font-mono text-xs">{w.subject_ref}</td>
                <td className="text-center">
                  {canManage ? <input type="number" min="5" defaultValue={w.interval_minutes} onBlur={(e) => setInterval(w, e.target.value)}
                    data-testid={`interval-${w.id}`} className="w-16 text-center border border-[#E7D9C6] rounded px-1 py-0.5 text-xs" /> : w.interval_minutes}
                </td>
                <td className="text-center"><span className="text-[11px] font-bold uppercase" style={{ color: STATE_C[w.enabled ? w.state : "paused"] }}>{w.enabled ? w.state : "paused"}</span></td>
                <td className="text-xs text-[#8A8175]">{w.last_success_at ? new Date(w.last_success_at).toLocaleString("en-GB") : "Never"}</td>
                <td className="text-center">{w.alerts_30d}</td>
                <td className="text-center">{w.precision === null ? "—" : `${Math.round(w.precision * 100)}%`}</td>
                {canManage && (
                  <td className="text-right pr-4 whitespace-nowrap">
                    <button onClick={() => runNow(w)} disabled={busy === w.id} data-testid={`run-${w.id}`} className="p-1.5 text-[#8A8175] hover:text-[#A5300F]">{busy === w.id ? <Spinner /> : <RefreshCw className="w-4 h-4" />}</button>
                    <button onClick={() => toggle(w)} data-testid={`toggle-${w.id}`} className="p-1.5 text-[#8A8175] hover:text-[#A5300F]">{w.enabled ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}</button>
                  </td>
                )}
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={canManage ? 8 : 7} className="py-10 text-center text-[#B5AC9E]">No watchers configured.</td></tr>}
          </tbody>
        </table>
      </Card>
    </div>
  );
}
